import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, MagnifyingGlass, Spinner, Buildings, ShieldCheck, GlobeHemisphereWest } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  resolveCrossBarrister,
  createCrossThread,
  type CrossChambersBarrister,
  type MessageThread,
} from '@/lib/messagingApi';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  onClose: () => void;
  onCreated: (thread: MessageThread) => void;
}

/**
 * Start a cross-workspace direct message with a barrister at ANOTHER chambers.
 * This is NOT email — the address only identifies the platform account; the conversation stays
 * entirely inside Jurivoxa. The corridor is fully isolated: nothing else from either workspace is
 * shared.
 */
export function CrossChambersModal({ onClose, onCreated }: Props) {
  const [email, setEmail] = useState('');
  const [resolved, setResolved] = useState<CrossChambersBarrister | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [message, setMessage] = useState('');

  const resolveMutation = useMutation({
    mutationFn: (addr: string) => resolveCrossBarrister(addr),
    onSuccess: (b) => {
      setResolved(b);
      setNotFound(!b);
    },
    onError: () => toast.error('Could not look up that address'),
  });

  const createMutation = useMutation({
    mutationFn: () => createCrossThread(resolved!.userId, message.trim() || undefined),
    onSuccess: (thread) => {
      toast.success('Conversation started');
      onCreated(thread);
    },
    onError: () => toast.error('Could not start the conversation'),
  });

  const handleFind = () => {
    const addr = email.trim();
    if (!addr) return;
    setResolved(null);
    setNotFound(false);
    resolveMutation.mutate(addr);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <Card className="my-auto flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-start gap-2.5">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:text-brand-400">
              <GlobeHemisphereWest size={17} weight="duotone" />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight text-foreground">
                Message a barrister at another chambers
              </div>
              <div className="text-xs text-muted-foreground">
                A secure in-app conversation with a barrister in a different workspace. Not email.
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Email lookup */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Barrister's verified email
            </label>
            <div className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setResolved(null); setNotFound(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleFind(); }}
                placeholder="e.g. daniel.mercer@independent.test"
                autoFocus
              />
              <Button variant="outline" size="sm" onClick={handleFind} disabled={!email.trim() || resolveMutation.isPending}>
                {resolveMutation.isPending
                  ? <Spinner size={14} className="animate-spin" />
                  : <><MagnifyingGlass size={14} className="mr-1" />Find</>}
              </Button>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              The email only identifies their Jurivoxa account. Your message is delivered in-app, never by email.
            </p>
          </div>

          {/* Not found */}
          {notFound && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 px-3.5 py-2.5 text-xs text-amber-800 dark:text-amber-300">
              No eligible barrister found for that email. Check the address, or ask them for the exact
              email on their Jurivoxa account. (Barristers in your own chambers appear in the normal
              “New message” picker.)
            </div>
          )}

          {/* Resolved barrister card */}
          {resolved && (
            <div className="rounded-xl border border-brand-200 bg-brand-50/40 px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-bold text-brand-700 dark:text-brand-300">
                  {resolved.firstName[0]}{resolved.lastName[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground">
                    {resolved.firstName} {resolved.lastName}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Buildings size={12} />
                    <span className="truncate">{resolved.workspaceName}</span>
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                  <ShieldCheck size={11} weight="fill" />External · Different chambers
                </span>
              </div>

              {/* First message */}
              <div className="mt-3">
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  First message <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your opening message…"
                  maxLength={4000}
                  rows={3}
                  className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-600"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border bg-muted/20 px-5 py-3">
          <span className="text-[11px] text-muted-foreground">
            Only this conversation is shared — never your matters, clients, diary or documents.
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={() => createMutation.mutate()} disabled={!resolved || createMutation.isPending}>
              {createMutation.isPending
                ? <><Spinner size={13} className="mr-1.5 animate-spin" />Starting…</>
                : 'Start conversation'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
